import { useEffect, useRef, useState } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { LogOut, Loader2 } from 'lucide-react'
import { ThemeToggle } from './ThemeToggle'

const AUTH_TIMEOUT_MS = 10000

export function InvestorLayout() {
  const { user, adminUser, isLoading, isMfaRequired, signOut } = useAuth()
  const navigate = useNavigate()
  const [authTimedOut, setAuthTimedOut] = useState(false)

  useSessionTimeout(adminUser?.role)

  useEffect(() => {
    if (!isLoading) {
      setAuthTimedOut(false)
      return
    }
    const timer = setTimeout(() => {
      if (isLoading) setAuthTimedOut(true)
    }, AUTH_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isLoading])

  // Log investor dashboard access (once per mount)
  const loggedRef = useRef(false)
  useEffect(() => {
    if (user && adminUser?.role === 'investor' && !loggedRef.current) {
      loggedRef.current = true
      supabase.rpc('log_admin_audit_event', {
        p_action: 'investor_dashboard_access',
        p_details: {},
        p_user_agent: navigator.userAgent,
      })
    }
  }, [user, adminUser])

  if (isLoading && !authTimedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-brand-fg" />
      </div>
    )
  }

  if (!user || authTimedOut) {
    return <Navigate to="/login" replace />
  }

  if (isMfaRequired) {
    return <Navigate to="/mfa-verify" replace />
  }

  // Role guard: only investors use this layout
  if (adminUser && adminUser.role !== 'investor') {
    return <Navigate to="/dashboard" replace />
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 h-16 bg-card/80 backdrop-blur border-b border-border">
        <div className="flex h-full items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <img src="/dextoro-logo.png" alt="dextoro" className="hidden dark:block h-7" />
            <img src="/dextoro-logo-black.png" alt="dextoro" className="block dark:hidden h-7" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="ghost" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="p-4 sm:p-6 max-w-7xl mx-auto">
        <Outlet />
      </main>
    </div>
  )
}
